/** Automated intake (§5.1, §5.2): read the county's photograph of this ground.
 *  Every failure is a specific banner that names the working manual path.
 *  Nothing here blocks hand work. */
import { useState } from 'preact/hooks';
import { createRecords, softDelete } from '../../core/db';
import { nowIso } from '../../core/ids';
import { ServiceError } from '../../core/net';
import { captureAerial, ringsBBox, type BBox } from '../../providers/aerial';
import { findCommonAreaTracts } from '../../providers/tracts';
import { aerialInventory } from '../../ai/tasks';
import { aiConfigured, explainAiError } from '../../ai/client';
import type { InventoryItem, Job } from '../../core/types';
import { toast } from '../state';

interface Props {
  job: Job;
  inventory: InventoryItem[];
  user: string;
  viewBox: [number, number, number, number] | null;
  /** which county flight the map is showing, so the reading matches the picture */
  basemap: string;
  onChanged: () => Promise<void>;
}

interface Note { kind: 'error' | 'info'; text: string }

export function IntakeBar({ job, inventory, user, viewBox, basemap, onChanged }: Props) {
  const [busy, setBusy] = useState<'' | 'aerial'>('');
  const [caveats, setCaveats] = useState<string | null>(null);
  const [note, setNote] = useState<Note | null>(null);

  /**
   * Read the county's photograph of this ground.
   *
   * The OpenStreetMap lookup below answered "nothing mapped inside this
   * boundary" for a real 20-acre HOA on 2026-07-25 — volunteers have not mapped
   * Phoenix subdivisions and are not going to. The county photographs every one
   * of them every year, so we look at the photograph instead.
   *
   * Counts only. Nothing here produces a measurement, and every row lands
   * unverified with its confidence attached (§2).
   */
  async function readAerial() {
    setBusy('aerial'); setNote(null); setCaveats(null);
    try {
      if (!(await aiConfigured())) {
        setNote({ kind: 'info', text: 'This needs the AI assistant. Add the proxy address and device passphrase in Settings — drawing and hand entry work without it.' });
        return;
      }
      const rings = job.parcel?.rings ?? [];
      let box: BBox;
      if (rings.length) box = ringsBBox(rings as [number, number][][]);
      else if (viewBox) box = viewBox;
      else { setNote({ kind: 'info', text: 'Outline the property first, or zoom the map to it — the photo needs somewhere to look.' }); return; }

      const year = /^mc(\d{4})$/.exec(basemap) ? Number(basemap.slice(2)) : 2026;

      // Field report 2026-07-27: "This is counting all the home parcels too."
      //
      // Greenway Parc 3 is 80 recorded parcels: 78 private houses, one city
      // parcel, and exactly ONE tract the association owns — 53,006 sq ft out of
      // a 20-acre plat. Reading the plat counted 78 people's back yards, which
      // David will never set foot on. So find the client's own ground first and
      // black out everything else before the photo is ever read.
      const platName = job.parcel?.provider === 'maricopa-subdivision'
        ? (job.parcel.label ?? '').split(' \u00b7 ')[0]
        : undefined;
      let maskRings: [number, number][][] = [];
      let owners: string[] = [];
      let tractSqFt = 0;
      try {
        const tracts = await findCommonAreaTracts(platName ? { subname: platName } : { bbox: box });
        for (const t of tracts) {
          maskRings.push(...t.rings);
          tractSqFt += t.measuredSqFt;
          if (!owners.includes(t.owner)) owners.push(t.owner);
        }
      } catch { /* county down: fall through to the unmasked path, which says so */ }

      if (!maskRings.length) {
        setNote({
          kind: 'info',
          text: 'The county lists no association-owned ground here, so this reads the whole outline — private lots included. Press \u201cShow the areas the HOA owns\u201d first if this is an HOA job, or expect to delete the neighbours\u2019 trees.',
        });
      }

      let cap;
      try {
        cap = await captureAerial(maskRings.length ? ringsBBox(maskRings) : box, { year, maskRings });
      } catch (e) {
        // Getting and preparing the picture is not the AI step. Reporting a
        // decode failure as "the AI call failed" sends the user to the wrong
        // place — the same mistake the mistyped proxy address used to make.
        if (e instanceof ServiceError) throw e;
        throw new ServiceError('County imagery', 'malformed',
          `The ${year} photo of this ground could not be prepared on this device. Try another flight year, or add inventory by hand.`);
      }
      // `place` is the roomiest free text the proxy passes to the model (160
      // chars), so it carries the one thing the model must understand about a
      // masked frame. The frame label itself is capped at 40.
      const place = (owners.length
        ? `${owners.join(' and ').slice(0, 70)} common area only. Black areas are other owners' land — do not count anything in them.`
        : (job.parcel?.label ?? job.siteAddress ?? '')).slice(0, 160) || undefined;
      const { data } = await aerialInventory(cap, place);

      // qty 0 means "I looked and there are none", which is not a row worth keeping
      data.items = data.items.filter((it) => it.qty > 0);
      if (!data.items.length) {
        setNote({ kind: 'info', text: `Nothing identifiable in the ${year} photo of this ground. Try another flight year, or add inventory by hand.` });
        return;
      }
      const rows = data.items.map((it) => ({
        jobId: job.id, cat: it.cat, species: it.species, qty: it.qty, cond: it.cond,
        notes: `${it.notes} (read from the ${year} county aerial${cap.masked ? ', client-owned ground only' : ', WHOLE OUTLINE \u2014 may include private lots'}, ${it.confidence} confidence)`,
        photoIds: [],
        provenance: { source: 'scan' as const, setBy: user, setAt: nowIso(), detail: `aerial-${year}` },
        verification: { verified: false },
      }));
      // Field report 2026-07-27: "I accidentally added the trees and stuff twice
      // so it's showing excess." A second read of the same ground replaces the
      // first — but only rows this tool put there and nobody has touched since.
      // A row someone verified on the walk-through, or corrected by hand, is
      // that person's number and is never thrown away by a machine.
      const superseded = inventory.filter((i) =>
        !i.deletedAt && i.provenance.source === 'scan' && !i.verification.verified);
      for (const old of superseded) await softDelete('inventoryItems', old.id);

      await createRecords('inventoryItems', rows, { snapshot: false });
      await onChanged();
      if (superseded.length) {
        setNote({
          kind: 'info',
          text: `Replaced ${superseded.length} row${superseded.length === 1 ? '' : 's'} from the previous read of this ground. Anything you had verified or corrected by hand was kept, and the replaced rows are under “show removed” in Site inventory.`,
        });
      }
      if (data.caveats) setCaveats(data.caveats);
      const trees = data.items.filter((i) => i.cat === 'tree' || i.cat === 'palm').reduce((n, i) => n + i.qty, 0);
      const ground = cap.masked
        ? `${(tractSqFt / 43560).toFixed(2)} acres of ${owners.length === 1 ? 'association' : 'association'} ground`
        : 'the whole outline, private lots included';
      toast(`${data.items.length} row${data.items.length === 1 ? '' : 's'} from the ${year} photo of ${ground}${trees ? `, ${trees} tree${trees === 1 ? '' : 's'} counted` : ''} — every one unverified until the walk-through.`);
    } catch (e) {
      if (e instanceof ServiceError) setNote({ kind: 'error', text: `${e.message}` });
      else setNote({ kind: 'error', text: `${explainAiError(e)}` });
    } finally { setBusy(''); }
  }

  return (
    <div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
        <button class="btn btn-small btn-primary" disabled={busy !== ''} onClick={() => void readAerial()}
          title="Sends the county's photograph of this ground to the AI, which counts trees and ground cover and guesses species. Counts only — it never measures.">
          {busy === 'aerial' ? 'Reading the photo…' : 'Count the trees from the photo (~$0.06)'}
        </button>
      </div>
      {/* The OpenStreetMap lookup used to sit here.
       *
       * Field report 2026-07-27: "Look up trees and lawns already mapped here
       * and count the trees from the photo do the same thing which is what
       * caused the duplicate site inventory."
       *
       * They were not the same — one read volunteer-drawn OpenStreetMap data,
       * the other reads the county's photograph — but the distinction bought
       * nothing here. Tested against a real 20-acre Phoenix HOA on 2026-07-25,
       * OpenStreetMap returned nothing at all; volunteers have not mapped these
       * subdivisions. So it was a button that found nothing, wrote into the same
       * list as the photo read, and gave two ways to put the same trees in
       * twice. The provider and its tests stay — this is the entry point going,
       * not the capability.
       */}
      {caveats && (
        <div class="banner banner-info" style="margin:8px 0 0;" role="status">
          <div><b>What the photo could not show:</b> {caveats}</div>
          <button class="btn btn-small btn-ghost" onClick={() => setCaveats(null)}>OK</button>
        </div>
      )}

      {note && (
        <div class={`banner ${note.kind === 'info' ? 'banner-info' : ''}`} style="margin:8px 0 0;" role={note.kind === 'error' ? 'alert' : 'status'}>
          <div>{note.text}</div>
          <button class="btn btn-small btn-ghost" onClick={() => setNote(null)}>OK</button>
        </div>
      )}
    </div>
  );
}
